import { cloneDeep, difference, isObject } from 'lodash'
import { createApp, h, resolveComponent } from 'vue'

import { forgeInvoke } from '@/utils/forge-helpers/general'
import { SUBSCRIPTION_PLANS } from '@/utils/web-app/plans-limitations'
import AppAuth from '@jira/layout/AppAuth'
import { PLUGIN_TYPES } from '@jira/util'
import { IS_DEVELOPMENT } from '@root/app-modes'
import { APP_PLATFORMS } from '@root/app-platforms'
import {
  BACKEND_VERSION,
  CREATE_AUTH_APP_PLUGIN_OPTIONS,
  JIRA_CONNECTED,
  LICENSE_TYPE,
  TEMPLATE_OPTIONS_KEYS,
  WEB_APP_CONNECTED,
  WEB_APP_LOGIN_URL,
  WEB_APP_URL
} from '@root/template-options-keys'

export const createAuthApp = (
  component,
  rootElement,
  additionalProps = {},
  appPlatform = APP_PLATFORMS.JIRA
) => {
  const { accessToken, refreshToken, pluginType, ...rest } = getPluginOptions(
    CREATE_AUTH_APP_PLUGIN_OPTIONS,
    rootElement
  )

  return createApp({
    name: 'AppAuthWrapper',
    components: {
      AppAuth,
      'main-component': component
    },

    // use AppAuth as layout(root component) for all entry-points
    render: () =>
      h(
        resolveComponent('AppAuth'),
        {
          accessToken,
          refreshToken,
          pluginType,
          pluginOptions: {
            pluginType,
            ...rest
          },
          appPlatform
        },
        {
          default: props => h(resolveComponent('main-component'), { ...props, ...additionalProps })
        }
      )
  })
}

export const getPluginOptions = (options, rootElement) => {
  const pluginOptions = { ...rootElement.dataset }
  return options.reduce((acc, val) => {
    acc[val] = pluginOptions[val]
    return acc
  }, {})
}

export const showDifferenceInKeys = (expected = [], pluginOptions = {}, appVariant = 'unknown') => {
  if (IS_DEVELOPMENT) return

  const production = JSON.parse(pluginOptions[TEMPLATE_OPTIONS_KEYS.PRODUCTION] ?? 'true')

  if (!production) {
    const actual = Object.keys(pluginOptions)

    console.log(appVariant)

    const missingKeys = difference(expected, actual)
    const extraKeys = difference(actual, expected)
    console.table(cloneDeep({ missingKeys, extraKeys }))
  }
}

const normalizePluginOptions = ({ pluginOptions = {} } = {}) => {
  if (!isObject(pluginOptions)) {
    throw new TypeError('normalizePluginOptions: pluginOptions must be an object')
  }

  const KEYS_MAPPER = {
    isJiraConnected: JIRA_CONNECTED,
    isWebAppConnected: WEB_APP_CONNECTED,
    backend_version: BACKEND_VERSION,
    webAppMainBaseUrl: WEB_APP_URL,
    webAppAuthBaseUrl: WEB_APP_LOGIN_URL,
    trackingLicenseInfo: LICENSE_TYPE
  }

  return Object.fromEntries(
    Object.entries(pluginOptions).map(([k, v]) => {
      const key = KEYS_MAPPER[k] ?? k
      const value = isObject(v) ? JSON.stringify(v) : String(v ?? '')

      return [key, value]
    })
  )
}

/**
 *
 * @param {HTMLElement} rootElement
 * @param {Array<string>} expectedOptions
 * @param {string} invokeMethodName
 * @returns {Promise<Record<string, string>>}
 */
export const getPluginOptionsFromForge = async (rootElement, expectedOptions, invokeMethodName) => {
  if (IS_DEVELOPMENT) {
    return getPluginOptions(expectedOptions, rootElement)
  }

  try {
    const pluginOptions = {
      ...(await forgeInvoke(invokeMethodName)),
      [TEMPLATE_OPTIONS_KEYS.PLUGIN_TYPE]: PLUGIN_TYPES.cloud,
      [TEMPLATE_OPTIONS_KEYS.JIRA_CONNECTED]: String(false),
      [TEMPLATE_OPTIONS_KEYS.SUBSCRIPTION_PLAN]: SUBSCRIPTION_PLANS.ENTERPRISE
    }

    const normalizedPluginOptions = normalizePluginOptions({
      pluginOptions
    })

    logPluginOptions(normalizedPluginOptions)

    return normalizedPluginOptions
  } catch (e) {
    console.error(e)

    return getPluginOptions(expectedOptions, rootElement)
  }
}

export const logPluginOptions = (pluginOptions = {}) => {
  if (IS_DEVELOPMENT) return

  if (!isObject(pluginOptions)) {
    throw new TypeError('logPluginOptions: pluginOptions must be an object')
  }

  const production = JSON.parse(pluginOptions[TEMPLATE_OPTIONS_KEYS.PRODUCTION] ?? 'true')

  if (!production) {
    console.table(pluginOptions)
  }
}
